import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Form, Spinner } from "react-bootstrap";
import Modal from "../../../../Utilities/DraggableModal";
import FormCol from "../../../FormCol";
import useFetch from "../../../../Utilities/useFetch";
import ReactSelect from "react-select";
import { useMsal } from "@azure/msal-react";
import FuncFetch from "../../../../Utilities/funcFetch";

function Arbitration({
    arbitration,
  arbitrationDecision,
    handleChange,
    readOnly,
    aYearFromNow,
    debtorID,
}) {
  const {data: arbitrators, loading: loadingArbitrators} = useFetch(true,`${process.env.REACT_APP_API_URL}/Claims/GetArbitrators`,"GET");
  const [arbitratorOptions, setArbitratorOptions] = useState([]);
  const { accounts, instance } = useMsal();
  const [showDecisionModal, setShowDecisionModal] = useState(false);
  const [savingDecision, setSavingDecision] = useState(false);
  const [saveDecisionError, setSaveDecisionError] = useState("");
  const [saveDecisionSuccess, setSaveDecisionSuccess] = useState("");
  const [decisionForm, setDecisionForm] = useState({
    collector: "",
    updatedDate: "",
    decision: "",
    liability: "",
    damagesArgument: "",
    damagesDecision: "",
    acArgument: "",
    acDecision: "",
    enteredBy: "",
    updatedBy: "",
    enteredDate: "",
  });

  const decisionData = useMemo(
    () =>
      arbitrationDecision ||
      arbitration?.arbitrationDecision ||
      arbitration?.arbitrationdecision ||
      arbitration?.arbitrationDecison ||
      arbitration?.arbitrationdecison ||
      {},
    [arbitration, arbitrationDecision]
  );
  const currentUser = accounts?.[0]?.username?.split("@")[0] || accounts?.[0]?.name || "";

  const getDecisionValue = (...keys) => {
    for (const key of keys) {
      const value = decisionData?.[key];
      if (value !== undefined && value !== null && `${value}` !== "") {
        return value;
      }
    }
    return "";
  };

  const formatDisplayDate = (dateValue) => {
    if (!dateValue || typeof dateValue !== "string") {
      return "";
    }

    return dateValue.includes("T") ? dateValue.split("T")[0] : dateValue;
  };

  const buildArbitrationDecisionObject = () => {
    const nowIso = new Date().toISOString();
    const resolvedUpdatedDate = decisionForm.updatedDate || nowIso;
    const resolvedUpdatedBy = decisionForm.collector || decisionForm.updatedBy || currentUser;

    return {
      ...decisionData,
      liability: decisionForm.liability,
      decision: decisionForm.decision,
      damagesArgument: decisionForm.damagesArgument,
      damagesDecision: decisionForm.damagesDecision,
      acArgument: decisionForm.acArgument,
      acDecision: decisionForm.acDecision,
      DamagesArgument: decisionForm.damagesArgument,
      DamagesDecision: decisionForm.damagesDecision,
      ACArgument: decisionForm.acArgument,
      ACDecision: decisionForm.acDecision,
      enteredDate: decisionForm.enteredDate || decisionData.enteredDate || nowIso,
      updatedDate: resolvedUpdatedDate,
      UpdatedDate: resolvedUpdatedDate,
      enteredBy: decisionForm.enteredBy || decisionData.enteredBy || currentUser,
      updatedBy: resolvedUpdatedBy,
      updatedby: resolvedUpdatedBy,
    };
  };

  const handleCloseDecisionModal = () => {
    handleChange("arbitrationdecision", buildArbitrationDecisionObject());
    setShowDecisionModal(false);
  };

  useEffect(() => {
    if (arbitrators) {
      setArbitratorOptions(arbitrators.map((arbitrator) => ({
        value: arbitrator.key,
        label: arbitrator.value,
      })));
    }
  }, [arbitrators]);

  useEffect(() => {
    if (!showDecisionModal) {
      return;
    }

    const nowIso = new Date().toISOString();
    setDecisionForm({
      collector:
        getDecisionValue("updatedBy", "updatedby", "enteredBy", "enteredby") ||
        currentUser,
      updatedDate:
        getDecisionValue("updatedDate", "UpdatedDate") ||
        nowIso,
      decision: getDecisionValue("decision", "Decision"),
      liability: getDecisionValue("liability", "Liability", "liabitlity", "Liabitlity"),
      damagesArgument: getDecisionValue("damagesArgument", "DamagesArgument"),
      damagesDecision: getDecisionValue("damagesDecision", "DamagesDecision"),
      acArgument: getDecisionValue("acArgument", "ACArgument"),
      acDecision: getDecisionValue("acDecision", "ACDecision"),
      enteredBy:
        getDecisionValue("enteredBy", "enteredby") ||
        currentUser,
      updatedBy:
        currentUser ||
        getDecisionValue("updatedBy", "updatedby") ||
        "",
      enteredDate:
        getDecisionValue("enteredDate", "EnteredDate") ||
        nowIso,
    });
    setSaveDecisionError("");
    setSaveDecisionSuccess("");
    // Initialize once when the modal opens; do not reset while user edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDecisionModal, currentUser]);

  const handleDecisionSave = () => {
    if (!debtorID) {
      setSaveDecisionError("Debtor ID is required.");
      return;
    }

    setSavingDecision(true);
    setSaveDecisionError("");
    setSaveDecisionSuccess("");

    const payload = {
      debtorID: debtorID,
      liability: decisionForm.liability,
      decision: decisionForm.decision,
      DamagesArgument: decisionForm.damagesArgument,
      DamagesDecision: decisionForm.damagesDecision,
      ACArgument: decisionForm.acArgument,
      ACDecision: decisionForm.acDecision,
      enteredDate: decisionForm.enteredDate || new Date().toISOString(),
      updatedDate: new Date().toISOString(),
      enteredBy: decisionForm.enteredBy || currentUser,
      updatedBy: decisionForm.updatedBy || currentUser,
    };

    FuncFetch(
      `${process.env.REACT_APP_API_URL}/claims/postarbitrationdecision`,
      null,
      "POST",
      payload,
      instance,
      accounts
    )
      .then(() => {
        const updatedDecision = {
          ...buildArbitrationDecisionObject(),
          updatedDate: payload.updatedDate,
          UpdatedDate: payload.updatedDate,
          updatedBy: payload.updatedBy,
          updatedby: payload.updatedBy,
        };
        handleChange("arbitrationDecision", updatedDecision);
        setDecisionForm((prev) => ({
          ...prev,
          updatedDate: payload.updatedDate,
          updatedBy: payload.updatedBy,
          collector: payload.updatedBy,
        }));
        setSaveDecisionSuccess("Arbitration decision saved.");
      })
      .catch((err) => {
        setSaveDecisionError(err?.message || "Failed to save arbitration decision.");
      })
      .finally(() => setSavingDecision(false));
  };

  return (
    <>
      <FormCol>
        <Form.Label>File Date</Form.Label>
        <Form.Control
          disabled={readOnly}
          value={arbitration.arbFileDate?.split("T")[0]}
          max={aYearFromNow}
          type="date"
          onChange={(e) => handleChange("arbFileDate", e.target.value)}
        />
      </FormCol>
      <FormCol>
        <Form.Label>Assigned To</Form.Label>
        {loadingArbitrators?
        <Spinner animation="border" size="sm" />
        :
        <ReactSelect
          isDisabled={readOnly}
          value={arbitratorOptions.find(option => option.value === arbitration.arbFiledBy)}
          onChange={(e) => handleChange("arbFiledBy", e.value)}
          options={arbitratorOptions}
          className="full-width"
        />}
      </FormCol>
      <FormCol>
        <Form.Label>Docket Number</Form.Label>
        <Form.Control
          disabled={readOnly}
          value={arbitration.arbDocketNumber}
          onChange={(e) => handleChange("arbDocketNumber", e.target.value)}
          onBlur={(e) => handleChange("arbDocketNumber", e.target.value.trimEnd())}
        />
      </FormCol>
      <FormCol>
        <Form.Label>Hearing Date</Form.Label>
        <Form.Control
          disabled={readOnly}
          value={arbitration.arbHearingDate?.split("T")[0]}
          max={aYearFromNow}
          type="date"
          onChange={(e) => handleChange("arbHearingDate", e.target.value)}
        />
      </FormCol>
      <FormCol>
        <Form.Label>Decision Date</Form.Label>
        <Form.Control
          disabled={readOnly}
          value={arbitration.arbDecisionDate?.split("T")[0]}
          max={aYearFromNow}
          type="date"
          onChange={(e) => handleChange("arbDecisionDate", e.target.value)}
        />
      </FormCol>
      <FormCol>
        <Form.Label>Decision Reviewed</Form.Label>
        <Form.Check
          type="checkbox"
          name="arbDecisionReviewed"
          checked={arbitration.arbDecisionReviewed}
          onChange={(e) => handleChange("arbDecisionReviewed", e.target.checked)}
          disabled={readOnly}
        />
      </FormCol>
      <FormCol>
        <Form.Label>Award Percentage</Form.Label>
        <Form.Control
          disabled={readOnly}
          type="number"
          value={arbitration.arbAwardPercentage}
          onChange={(e) => handleChange("arbAwardPercentage", e.target.value)}
        />
      </FormCol>
        <FormCol>
          <Form.Label>Award Amount</Form.Label>
          <Form.Control
            disabled={readOnly}
            type="number"
            step="0.01"
            value={arbitration.arbAwardAmount}
            onChange={(e) => handleChange("arbAwardAmount", e.target.value)}
          />
        </FormCol>
      <FormCol>
        <Form.Label>Received Date</Form.Label>
        <Form.Control
          disabled={readOnly}
          value={arbitration.arbReceivedDate?.split("T")[0]}
          max={aYearFromNow}
          type="date"
          onChange={(e) => handleChange("arbReceivedDate", e.target.value)}
        />
      </FormCol>
      <FormCol>
        <Form.Label>Recorded Statement Date</Form.Label>
        <Form.Control
          disabled={readOnly}
          value={arbitration.arbRecordedStatementDate?.split("T")[0]}
          max={aYearFromNow}
          type="date"
          onChange={(e) => handleChange("arbRecordedStatementDate", e.target.value)}
        />
      </FormCol>
      <FormCol>
        <Form.Label>Recorded Statement Form Date Sent</Form.Label>
        <Form.Control
          disabled={readOnly}
          value={arbitration.arbRecordedStatementFormDateSent?.split("T")[0]}
          max={aYearFromNow}
          type="date"
          onChange={(e) => handleChange("arbRecordedStatementFormDateSent", e.target.value)}
        />
      </FormCol>
      <FormCol>
        <Form.Label>Statement Needed</Form.Label>
        <Form.Check
          type="checkbox"
          checked={arbitration.arbStatementNeeded}
          onChange={(e) => handleChange("arbStatementNeeded", e.target.checked)}
          disabled={readOnly}
        />
      </FormCol>
      <FormCol>
        <Form.Label>Statement Received</Form.Label>
        <Form.Check
          type="checkbox"
          checked={arbitration.arbRecordedStatementReceived}
          onChange={(e) => handleChange("arbRecordedStatementReceived", e.target.checked)}
          disabled={readOnly}
        />
      </FormCol>
      <div className="w-100 d-flex justify-content-end mt-2">
        <Button variant="outline-primary" onClick={() => setShowDecisionModal(true)} disabled={readOnly}>
          Arbitration Decision
        </Button>
      </div>

      <Modal show={showDecisionModal} onHide={handleCloseDecisionModal} centered size="xl" scrollable>
        <Modal.Header closeButton>
          <Modal.Title>Arbitration Decision</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Collector</Form.Label>
              <Form.Control readOnly value={decisionForm.collector} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Date</Form.Label>
              <Form.Control readOnly value={formatDisplayDate(decisionForm.updatedDate)} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Liability Decision</Form.Label>
              <Form.Control
                as="textarea"
                rows={8}
                value={decisionForm.decision}
                onChange={(e) => setDecisionForm((prev) => ({ ...prev, decision: e.target.value }))}
                style={{ minHeight: "180px" }}
              />
            </Form.Group>
          <Form.Group className="mb-3">
              <Form.Label>Damages Decision</Form.Label>
              <Form.Control
                as="textarea"
                rows={8}
                value={decisionForm.damagesDecision}
                onChange={(e) => setDecisionForm((prev) => ({ ...prev, damagesDecision: e.target.value }))}
                style={{ minHeight: "180px" }}
              />
            </Form.Group>    <Form.Group className="mb-3">
              <Form.Label>Liability Argument</Form.Label>
              <Form.Control
                as="textarea"
                rows={8}
                value={decisionForm.liability}
                onChange={(e) => setDecisionForm((prev) => ({ ...prev, liability: e.target.value }))}
                style={{ minHeight: "180px" }}
              />
            </Form.Group>
        
               <Form.Group className="mb-3">
              <Form.Label>Damages Argument</Form.Label>
              <Form.Control
                as="textarea"
                rows={8}
                value={decisionForm.damagesArgument}
                onChange={(e) => setDecisionForm((prev) => ({ ...prev, damagesArgument: e.target.value }))}
                style={{ minHeight: "180px" }}
              />
            </Form.Group>
           
            <Form.Group className="mb-3">
              <Form.Label>Adverse Carrier Liability Argument</Form.Label>
              <Form.Control
                as="textarea"
                rows={8}
                value={decisionForm.acArgument}
                onChange={(e) => setDecisionForm((prev) => ({ ...prev, acArgument: e.target.value }))}
                style={{ minHeight: "180px" }}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Adverse Carrier Damages Argument</Form.Label>
              <Form.Control
                as="textarea"
                rows={8}
                value={decisionForm.acDecision}
                onChange={(e) => setDecisionForm((prev) => ({ ...prev, acDecision: e.target.value }))}
                style={{ minHeight: "180px" }}
              />
            </Form.Group>
            {saveDecisionError ? <Alert variant="danger">{saveDecisionError}</Alert> : null}
            {saveDecisionSuccess ? <Alert variant="success">{saveDecisionSuccess}</Alert> : null}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={handleDecisionSave} disabled={savingDecision}>
            {savingDecision ? <Spinner animation="border" size="sm" /> : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default Arbitration;